package org.maplibre.compose.style

import androidx.compose.runtime.State
import kotlin.time.Duration
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.doubleOrNull
import org.maplibre.compose.camera.CameraAnimation

/**
 * The platform's animator duration scale: the value of Android's
 * `Settings.Global.ANIMATOR_DURATION_SCALE`, or 1f where the platform has no such setting. A scale
 * of zero means the user asked for no animations. The state follows the setting, so a composition
 * that reads it recomposes when the setting changes.
 */
internal expect val systemAnimatorDurationScaleState: State<Float>

/** The current value of [systemAnimatorDurationScaleState]. */
internal fun systemAnimatorDurationScale(): Float = systemAnimatorDurationScaleState.value

/**
 * Returns [this] multiplied by [scale], or [this] unchanged when the scale is 1f. A scale of zero
 * returns [Duration.ZERO], which the camera treats as a jump.
 */
internal fun Duration.scaledBy(scale: Float): Duration {
  requireScale(scale)
  if (scale == 1f) return this
  return this * scale.toDouble()
}

/**
 * Returns [this] with its timing multiplied by [scale], or [this] unchanged when the scale is 1f. A
 * flight without a duration divides its speed by [scale] instead. A scale of zero makes the
 * transition a jump.
 */
internal fun CameraAnimation.scaledBy(scale: Float): CameraAnimation {
  requireScale(scale)
  if (scale == 1f) return this
  val animation = this
  return when (animation) {
    is CameraAnimation.Ease ->
      CameraAnimation.Ease(from = animation) { duration = animation.duration.scaledBy(scale) }
    is CameraAnimation.Fly ->
      when (val duration = animation.duration) {
        null ->
          if (scale == 0f) CameraAnimation.Fly(from = animation) { this.duration = Duration.ZERO }
          else CameraAnimation.Fly(from = animation) { speed = animation.speed / scale }
        else -> CameraAnimation.Fly(from = animation) { this.duration = duration.scaledBy(scale) }
      }
    else -> animation
  }
}

/**
 * Returns [this] with duration and delay multiplied by [scale], or [this] unchanged when the scale
 * is 1f. A scale of zero zeroes both fields, which applies property changes instantly.
 */
internal fun TransitionOptions.scaledBy(scale: Float): TransitionOptions {
  requireScale(scale)
  if (scale == 1f) return this
  return TransitionOptions(duration = duration * scale.toDouble(), delay = delay * scale.toDouble())
}

/**
 * Returns [this] with the `duration` and `delay` of every `<property>-transition` object multiplied
 * by [scale], for the paint object of a layer and for the light and sky objects. Other keys, and a
 * transition object without a field, pass through unchanged.
 */
internal fun JsonObject.withScaledTransitions(scale: Float): JsonObject {
  requireScale(scale)
  if (scale == 1f) return this
  return JsonObject(
    mapValues { (name, value) ->
      if (name.endsWith(TransitionSuffix) && value is JsonObject) value.scaledTransition(scale)
      else value
    },
  )
}

private fun JsonObject.scaledTransition(scale: Float): JsonObject =
  JsonObject(
    mapValues { (field, value) ->
      val millis = (value as? JsonPrimitive)?.doubleOrNull
      if (millis != null && (field == "duration" || field == "delay")) {
        JsonPrimitive(millis * scale)
      } else {
        value
      }
    },
  )

private fun requireScale(scale: Float) {
  require(scale.isFinite() && scale >= 0f) {
    "Animator duration scale must be finite and not negative: $scale"
  }
}
